import { dataManager } from 'singletons';
import { AppDataState } from 'reducers/data';
import { clearStatus } from 'actions/status';

export const RECEIVE_ACCOUNTS = 'RECEIVE_ACCOUNTS';
export const RECEIVE_ACCOUNT = 'RECEIVE_ACCOUNT';
export const RECEIVE_PRODUCTS = 'RECEIVE_PRODUCTS';
export const SET_VISIBLE_ACCOUNTS = 'SET_VISIBLE_ACCOUNTS';
export const SET_CURRENT_PRODUCT = 'SET_CURRENT_PRODUCT';
export const SET_CURRENT_ACCOUNT = 'SET_CURRENT_ACCOUNT';
export const RECEIVE_USERS = 'RECEIVE_USERS';
export const RECEIVE_USER = 'RECEIVE_USER';
export const RECEIVE_ROLES = 'RECEIVE_ROLES';
export const SET_VISIBLE_USERS = 'SET_VISIBLE_USERS';

export const fetchRoles = (): ActionCreator<void> => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        const availableRoles = await dataManager.getRoles();
        await dispatch(receiveData(RECEIVE_ROLES, availableRoles));
    };
};

export const fetchAccountList = (): ActionCreator<void> => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        // start by getting account list
        const accountList = await dataManager.getAccountList();
        await dispatch(receiveData(RECEIVE_ACCOUNTS, accountList));

        // iterate over accounts to get available products
        let productNames = [] as string[];
        if (accountList) {
            const productSet = new Set<string>();
            accountList.forEach(account => {
                if (account.subAccounts) {
                    account
                        .subAccounts
                        .filter(subAccount => subAccount != null)
                        .forEach(subAccount => {
                            productSet.add(subAccount.type);
                    });
                }
            });

            productNames = Array.from(productSet).sort();
            await dispatch(receiveData(RECEIVE_PRODUCTS, productNames));
        }

        // re-select or choose new product to trigger side-effects
        const currentProduct = getState().currentProduct;
        const newProduct = productNames.indexOf(currentProduct) !== -1 ?
            currentProduct :
            productNames[0] || '';
        await dispatch(selectProduct(newProduct));
    };
};

export const selectProduct = (productName: string): ActionCreator<void> => {
    return async (dispatch: Function, getState: () => AppDataState): Promise<void> => {
        await dispatch(clearStatus());
        await dispatch(receiveData(SET_CURRENT_PRODUCT, productName));

        // filter visible accounts based on selected product
        const allAccounts = getState().accounts;
        const productAccounts = allAccounts ?
            Object.keys(allAccounts).reduce((acc, accountId): MasterAccount[] => {
                const account = allAccounts[accountId];
                let hasProduct = false;
                account
                    .subAccounts
                    .filter(subAccount => subAccount != null)
                    .forEach(subAccount => {
                        if (subAccount.type === productName) {
                            hasProduct = true;
                        }
                });

                if (hasProduct) {
                    acc.push(account);
                }

                return acc;
            }, [] as MasterAccount[]) :
            [];

        await dispatch(receiveData(SET_VISIBLE_ACCOUNTS, productAccounts));

        // need to then see if our current account ID is still valid. If not, then
        // pick a new current account. re-select regardless to trigger side effects
        const currentAccountId = getState().currentAccountId;
        const newAccountId = getState().visibleAccounts[currentAccountId] == null ?
            Object.keys(getState().visibleAccounts).sort()[0] || '' :
            currentAccountId;

        await dispatch(selectAccount(newAccountId));
    };
};

export const selectAccount = (accountId: string): ActionCreator<void> => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        await dispatch(clearStatus());
        await dispatch(receiveData(SET_CURRENT_ACCOUNT, accountId));
        await Promise.all([
            dispatch(fetchUsers(accountId)),
            dispatch(fetchMasterAccount(accountId))
        ]) ;
    };
};

export const fetchUsers = (accountId: string) => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        // clear out stale users before making request
        await dispatch(receiveData(RECEIVE_USERS, null));

        const users = await dataManager.getUsers(accountId);
        await dispatch(receiveData(RECEIVE_USERS, users));
    };
};

export const fetchMasterAccount = (accountId: string) => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        // clear out stale users before making request
        await dispatch(receiveData(RECEIVE_ACCOUNT, null));

        const account = await dataManager.getMasterAccount(accountId);
        await dispatch(receiveData(RECEIVE_ACCOUNT, account));
    };
};

export const fetchUser = (userId: string) => {
    return async (dispatch: Function, getState: Function): Promise<void> => {
        const user = await dataManager.getUser(userId);
        await dispatch(receiveData(RECEIVE_USER, user));
    };
};

export const filterUsers = (searchString: string): ActionCreator<void> => {
    return async (dispatch: Function, getState: () => AppDataState): Promise<void> => {
        const users = getState().users || [];
        const filteredUsers = searchString ?
            users.filter(user => {
                return (user.displayName.indexOf(searchString) !== -1 ||
                    user.userName.indexOf(searchString) !== -1);
            }) :
            users;

        await dispatch(receiveData(SET_VISIBLE_USERS, filteredUsers));
    };
};

const receiveData = (type: string, data: Object | null): DataAction => {
    return {
        type, data
    };
};



// WEBPACK FOOTER //
// ./src/actions/data.ts